/**
 * EleveLayout — Sidebar Élève au style codevoiture.fr (Phase 30).
 * Palette rouge/rose, brand "Mon apprentissage", 8 items dans la sidebar.
 */
import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, TrendingUp, ClipboardList, ClipboardCheck,
  ShieldAlert, Calendar, Mail, LogOut, ExternalLink, GraduationCap, MessageCircle, UserCog, Clapperboard,
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { RefreshButton } from './RefreshButton';
import { BottomNav } from './BottomNav';
import api from '../api';

// Back-office élève = boîte à outils personnelle uniquement.
// Les cours sont accessibles depuis le site public (Magazine, /bibliotheque).
const NAV = [
  { to: '/eleve/dashboard', label: 'Tableau de bord', icon: LayoutDashboard, exact: true, testid: 'eleve-nav-dashboard' },
  { to: '/eleve/mes-shorts', label: 'Mes Shorts', icon: Clapperboard, testid: 'eleve-nav-mes-shorts', badgeKey: 'shorts', highlight: true },
  { to: '/eleve/progression', label: 'Ma progression', icon: TrendingUp, testid: 'eleve-nav-progression' },
  { to: '/eleve/livret', label: 'Mon livret', icon: ClipboardList, testid: 'eleve-nav-livret' },
  { to: '/eleve/bilans', label: 'Mes bilans', icon: ClipboardCheck, testid: 'eleve-nav-bilans' },
  { to: '/eleve/permis-blanc', label: 'Permis blanc', icon: ShieldAlert, testid: 'eleve-nav-permis-blanc' },
  { to: '/eleve/planning', label: 'Mon planning', icon: Calendar, testid: 'eleve-nav-planning' },
  { to: '/eleve/mes-moniteurs', label: 'Mes moniteurs', icon: UserCog, testid: 'eleve-nav-mes-moniteurs' },
  { to: '/eleve/messages', label: 'Messages', icon: Mail, testid: 'eleve-nav-messages' },
  { to: '/eleve/contact-admin', label: 'Contacter l\'admin', icon: MessageCircle, testid: 'eleve-nav-contact-admin' },
];

export default function EleveLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useApp() || {};
  const [badges, setBadges] = useState({ shorts: 0 });
  const onLogout = () => { if (logout) logout(); navigate('/login'); };

  // Compte le nombre de Shorts en attente de modération (pour badge sidebar)
  useEffect(() => {
    (async () => {
      try {
        const mine = await api.shortsMine();
        const pending = Array.isArray(mine) ? mine.filter((s) => s.status === 'pending').length : 0;
        setBadges((prev) => ({ ...prev, shorts: pending }));
      } catch (_) { /* silent */ }
    })();
  }, [pathname]);

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col lg:flex-row" data-testid="eleve-layout">
      {/* ===== SIDEBAR ===== */}
      <aside className="lg:w-60 lg:min-h-screen shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0d0d0e] flex lg:flex-col">
        <div className="hidden lg:flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
          <div className="w-9 h-9 rounded-xl bg-rose-600 text-white flex items-center justify-center shadow-lg shadow-rose-900/40">
            <GraduationCap size={16} />
          </div>
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.22em] text-rose-400 font-bold">code2roues.fr</div>
            <div className="text-white text-[14px] font-semibold leading-tight">Mon apprentissage</div>
          </div>
        </div>
        <nav className="flex lg:flex-col gap-1 p-2 lg:p-3 overflow-x-auto lg:overflow-visible flex-1 items-center lg:items-stretch" data-testid="eleve-sidebar-nav">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = n.external ? false : (n.exact ? pathname === n.to : pathname.startsWith(n.to));
            const externalProps = n.external ? { target: '_blank', rel: 'noopener noreferrer' } : {};
            // Item "highlight" (ex. Ma bibliothèque) — mise en avant visuelle même inactif
            const highlightClass = n.highlight && !active
              ? 'bg-gradient-to-r from-rose-500/15 to-transparent border border-rose-500/25 text-white hover:from-rose-500/25'
              : '';
            const count = n.badgeKey ? badges[n.badgeKey] : 0;
            return (
              <Link
                key={n.to}
                to={n.to}
                {...externalProps}
                data-testid={n.testid}
                className={`flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold whitespace-nowrap transition ${
                  active ? 'bg-rose-600 text-white shadow-md shadow-rose-900/40' : highlightClass || 'text-white/65 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={15} className="shrink-0" />
                <span className="flex-1">{n.label}</span>
                {count > 0 && (
                  <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10.5px] font-bold inline-flex items-center justify-center ${active ? 'bg-white text-rose-600' : 'bg-rose-600 text-white'}`}>
                    {count}
                  </span>
                )}
                {n.external && <ExternalLink size={11} className="shrink-0 text-white/40" />}
              </Link>
            );
          })}
        </nav>
        <div className="hidden lg:block p-3 border-t border-white/10 space-y-1">
          <Link to="/" data-testid="eleve-back-to-site" className="flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold text-white/60 hover:bg-white/5 hover:text-white transition">
            <ExternalLink size={14} /> Retour à l&apos;accueil
          </Link>
          <button onClick={onLogout} data-testid="eleve-logout" className="w-full flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold text-white/65 hover:bg-red-950/40 hover:text-red-300 transition">
            <LogOut size={14} /> Déconnexion
          </button>
          {user && <div className="px-3.5 pt-2 pb-1 text-white/35 text-[11px] truncate">{user.email}</div>}
        </div>
      </aside>

      {/* ===== CONTENU ===== */}
      <div className="flex-1 min-w-0">
        <header className="h-12 lg:h-16 border-b border-white/10 bg-[#0d0d0e]/60 backdrop-blur flex items-center justify-between px-4 lg:px-8">
          <div className="text-white/45 text-[12px] uppercase tracking-[0.2em] truncate">Espace élève</div>
          <div className="flex items-center gap-2">
            <RefreshButton variant="eleve" />
            <div className="hidden lg:flex items-center gap-2 text-white/60 text-[12px]">
              <Mail size={12} />
              <span>{user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Moi'}</span>
            </div>
            <button onClick={onLogout} className="lg:hidden text-red-300 text-[12px] inline-flex items-center gap-1">
              <LogOut size={12} /> Quitter
            </button>
          </div>
        </header>
        <main className="px-4 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-8" data-testid="eleve-main">
          <Outlet />
        </main>
      </div>
      {/* BottomNav mobile — accès rapide Plateau / Circu / Tutos / Mag depuis le BO */}
      <BottomNav />
    </div>
  );
}
